import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { connectScreenSocket } from '../services/screenSocketOwner';
import { AdminLogin } from '../components/ScreenManager';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { useConfirm } from '../components/DialogProvider';
import { enterFullscreen } from '../utils/fullscreen';
import { getDeniedReason, getLinkedPin, getValidStoredInstance, isPinSessionValid, markPinSession } from '../utils/pinSession';
import { buildEntryUrl, fetchParticipantChain } from '../utils/participantEntry';
import { getTown, setTown } from '../utils/participantTown';
import TownPicker from '../components/auth/TownPicker';
import API_ENDPOINTS from '../config/api';
import {
  SelectorContainer, SelectorTitle, SelectorSubtitle, DropdownContainer, DropdownButton,
  DropdownArrow, DropdownMenu, DropdownItem, InstanceIcon, ScreenGrid, WallSection, WallLabel,
  ScreenPair, ScreenButton, TakenBadge, AvailableBadge, TransitionOverlay, StatusText,
  SectionLabel, IPadGrid, IPadButton,
} from './ScreenClient.styles';
import { VALID_IPAD_CODES, WALLS, WALL_COMPASS_KEY } from './screenCodes';

/**
 * ScreenSelector - Select which screen this browser tab represents
 *
 * Flow:
 * 1. Check localStorage for saved instance
 * 2. If no saved instance → show AdminLogin
 * 3. After admin PIN login → show instance selector
 * 4. After regular PIN login → use PIN's instance directly
 */
const ScreenSelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [screenStatuses, setScreenStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // A team workshop asks the town once per device before entering: { instanceId, chain }
  const [townPick, setTownPick] = useState(null);
  // Saved screen code waiting for a free/occupied verdict before auto-rejoin
  const [pendingScreen, setPendingScreen] = useState(null);
  const [statusesReady, setStatusesReady] = useState(false);
  const [previousCode, setPreviousCode] = useState(null);
  const socketRef = useRef(null);
  const dropdownRef = useRef(null);

  /**
   * Participant entry: URL → PIN → town → begin. The chain says whether the
   * workshop has teams (towns); a device that has none stored picks one first
   * (TownPicker), then lands on the chain's entry module.
   */
  const enterInstance = async (instanceId) => {
    const chain = await fetchParticipantChain(instanceId);
    if (chain.teams?.length && !getTown(instanceId)) {
      setTownPick({ instanceId, chain });
      return;
    }
    navigate(buildEntryUrl(chain, instanceId), { replace: true });
  };

  const pickTown = (team) => {
    if (!townPick) return;
    setTown(townPick.instanceId, team);
    const { instanceId, chain } = townPick;
    setTownPick(null);
    navigate(buildEntryUrl(chain, instanceId), { replace: true });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch instances on mount and check auth state
  useEffect(() => {
    const fetchInstances = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.INSTANCES());
        const data = await response.json();
        // Only show active workshop instances
        const activeInstances = data.filter(i => ['active', 'running', 'pre-workshop', 'post-workshop', 'draft'].includes(i.status));
        setInstances(activeInstances);

        // PIN session lasts 4 days; expired / missing timestamp, a PIN link (?pin=, it names its own instance) or a refused link (?denied=, #1116) → login
        const savedInstance = (getLinkedPin() || getDeniedReason()) ? null : getValidStoredInstance();
        const savedAuth = isPinSessionValid() && !getLinkedPin() && !getDeniedReason() ? localStorage.getItem('kuutio-screen-admin') : null;
        const savedScreen = isPinSessionValid() ? localStorage.getItem('kuutio-screen-code') : null;

        if (savedInstance && activeInstances.some(i => i.id === savedInstance)) {
          // Screen control disabled → the participant entry of the chain
          const inst = activeInstances.find(i => i.id === savedInstance);
          if (inst && !inst.settings?.screenControl) {
            await enterInstance(savedInstance);
            return;
          }

          // Device already paired to an active instance
          setSelectedInstance(savedInstance);
          localStorage.setItem('kuutio-instance', savedInstance);
          setNeedsAuth(false);

          // Saved screen code: rejoin silently ONLY if the code is free.
          // A TV rebooting finds its old code offline and recovers touchless;
          // an admin laptop finds the code live on the real TV and gets the
          // chooser instead of silently kicking it (learned the hard way).
          if (savedScreen) {
            setPendingScreen(savedScreen);
          }
        } else if (savedAuth) {
          // Admin PIN remembered — skip login, show instance selector
          setIsAdmin(true);
          setNeedsAuth(false);
        } else {
          // No saved config - require admin login
          setNeedsAuth(true);
        }
      } catch (err) {
        console.error('Failed to fetch instances:', err);
        // On error, still require auth
        setNeedsAuth(true);
      } finally {
        setLoading(false);
      }
    };
    fetchInstances();
  }, []);

  // Connect to socket to get screen statuses
  useEffect(() => {
    if (!selectedInstance) return;

    const socket = connectScreenSocket(selectedInstance, 'selector', 'screen-selector');
    socketRef.current = socket;

    const requestStatuses = () => {
      socket.emit('screen-status-request', { instanceId: selectedInstance });
    };

    const onStatuses = (data) => {
      const statuses = {};
      (data.screens || []).forEach(s => {
        statuses[s.code] = s.isOnline;
      });
      setScreenStatuses(statuses);
      setStatusesReady(true);
    };

    const onStatus = (data) => {
      setScreenStatuses(prev => ({
        ...prev,
        [data.code]: data.isOnline,
      }));
    };

    socket.on('connect', requestStatuses);
    socket.on('screen-statuses', onStatuses);
    socket.on('screen-status', onStatus);

    // Poll for updates
    const interval = setInterval(requestStatuses, 5000);

    return () => {
      clearInterval(interval);
      // Remove only our handlers; don't disconnect - the ScreenClient will take over the socket (#1427)
      socket.off('connect', requestStatuses);
      socket.off('screen-statuses', onStatuses);
      socket.off('screen-status', onStatus);
    };
  }, [selectedInstance]);

  // Auto-rejoin verdict for a saved screen code
  useEffect(() => {
    if (!pendingScreen || !selectedInstance) return undefined;
    const rejoin = () => navigate(`/screen/${pendingScreen}?instance=${selectedInstance}`);
    if (statusesReady) {
      if (!screenStatuses[pendingScreen]) {
        rejoin(); // free (or offline) → touchless recovery
      } else {
        setPreviousCode(pendingScreen); // live elsewhere → stay on chooser
        setPendingScreen(null);
      }
      return undefined;
    }
    // Statuses never arrived (backend hiccup): preserve TV auto-recovery
    const fallback = setTimeout(rejoin, 4000);
    return () => clearTimeout(fallback);
  }, [pendingScreen, statusesReady, screenStatuses, selectedInstance, navigate]);

  const handleScreenSelect = async (code) => {
    if (!selectedInstance) {
      console.error('No instance selected');
      return;
    }
    if (screenStatuses[code]) {
      // Screen is taken, confirm override
      if (!(await confirm({ message: t('screenClient.screenTakeoverConfirm', { code }), destructive: true }))) {
        return;
      }
    }
    // The tap is a user gesture: take the browser fullscreen right here —
    // TVs have no F11, and fullscreen is only grantable on a gesture.
    // enterFullscreen() also covers webkit/ms prefixes (iPadOS Safari has no
    // unprefixed Element.requestFullscreen) and logs why it failed.
    enterFullscreen();
    // Save screen code for auto-recovery after reboot
    localStorage.setItem('kuutio-screen-code', code);
    // Trigger fade to black, then navigate
    console.log(`Navigating to /screen/${code}?instance=${selectedInstance}`);
    setTransitioning(true);
    setTimeout(() => {
      navigate(`/screen/${code}?instance=${selectedInstance}`);
    }, 400); // Match the CSS transition duration
  };

  // Handle admin login completion
  const handleAdminLogin = async ({ instanceId, isAdmin: loginIsAdmin }) => {
    console.log('[ScreenSelector] Login result:', { instanceId, isAdmin: loginIsAdmin });

    if (loginIsAdmin) {
      // Admin PIN - show instance selector, remember auth
      markPinSession();
      localStorage.setItem('kuutio-screen-admin', 'true');
      setIsAdmin(true);
      setNeedsAuth(false);
    } else if (instanceId) {
      // Regular PIN — route based on the instance's screen-control toggle.
      // settings.screenControl defaults to false. When false, PIN-authed
      // participants go straight to /impactpath (the contribution surface).
      // When true, we fall through to the screen-code selector so this
      // device can drive one of the big-wall display screens instead.
      //
      // Two localStorage keys on purpose:
      //  - kuutio-instance              — ScreenClient's own auto-resume on /screen
      //  - kuutio-participant-instance  — what InstanceContext reads as a fallback
      //                                    when ?kid= isn't on the URL. Without
      //                                    this second one, navigating from
      //                                    /impactpath to /pivotpoints (etc.)
      //                                    via UI controls that drop the query
      //                                    string would lose instance context.
      markPinSession();
      localStorage.setItem('kuutio-instance', instanceId);
      localStorage.setItem('kuutio-participant-instance', instanceId);
      const inst = instances.find(i => i.id === instanceId);
      if (!inst?.settings?.screenControl) {
        try {
          await enterInstance(instanceId);
          return;
        } catch (err) {
          // No participant module to land on: keep the device as a screen
          console.error('Participant entry failed:', err);
        }
      }
      setSelectedInstance(instanceId);
      setNeedsAuth(false);
    }
  };

  if (loading) {
    return (
      <SelectorContainer>
        <StatusText>{t('screenClient.loading')}</StatusText>
      </SelectorContainer>
    );
  }

  // Step 3 of the laptop path: the town, asked once per device
  if (townPick) {
    const inst = instances.find(i => i.id === townPick.instanceId);
    return <TownPicker teams={townPick.chain.teams} instanceTitle={inst?.title || ''} onPick={pickTown} />;
  }

  // Show AdminLogin if no saved instance
  if (needsAuth) {
    return <AdminLogin onLogin={handleAdminLogin} />;
  }

  const selectedInstanceData = instances.find(i => i.id === selectedInstance);

  const handleInstanceSelect = (instanceId) => {
    setSelectedInstance(instanceId);
    setDropdownOpen(false);
    // Save to localStorage for device-wide persistence
    localStorage.setItem('kuutio-instance', instanceId);
  };

  return (
    <>
      <TransitionOverlay $visible={transitioning} />
      <SelectorContainer>
        <SelectorTitle>{t('screenClient.selectDisplay')}</SelectorTitle>
        <SelectorSubtitle>{t('screenClient.selectInstanceAndDisplay')}</SelectorSubtitle>

        {previousCode && (
          <div style={{
            margin: '12px auto', padding: '10px 18px', maxWidth: 560, borderRadius: 8,
            border: '1px solid rgba(245, 158, 11, 0.6)', background: 'rgba(245, 158, 11, 0.12)',
            color: '#fbbf24', fontSize: 14, textAlign: 'center',
          }}>
            Tämä laite oli näyttö <strong>{previousCode}</strong>, joka on nyt käytössä toisella
            laitteella — automaattista liittymistä ei tehty. Näytön valinta alta korvaa sen haltijan.
          </div>
        )}

        {/* Custom instance dropdown */}
        <DropdownContainer ref={dropdownRef}>
        <DropdownButton
          $hasValue={!!selectedInstance}
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          <span>
            {selectedInstanceData?.title || t('screenClient.selectInstancePlaceholder')}
          </span>
          <DropdownArrow $isOpen={dropdownOpen}>▼</DropdownArrow>
        </DropdownButton>

        {dropdownOpen && (
          <DropdownMenu>
            {instances.map(inst => (
              <DropdownItem
                key={inst.id}
                $selected={selectedInstance === inst.id}
                onClick={() => handleInstanceSelect(inst.id)}
              >
                <InstanceIcon>🎯</InstanceIcon>
                <span>{inst.title}</span>
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      </DropdownContainer>

      {/* Change instance link */}
      {selectedInstance && (
        <button
          onClick={() => {
            localStorage.removeItem('kuutio-instance');
            localStorage.removeItem('kuutio-screen-code');
            setSelectedInstance(null);
            setIsAdmin(true);
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#666',
            fontSize: '13px',
            cursor: 'pointer',
            marginBottom: '24px',
            padding: '4px 8px',
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          }}
        >
          {t('screenClient.changeInstance')}
        </button>
      )}

      {/* Screen grid - always visible */}
      {!selectedInstance && (
        <div style={{
          color: '#666',
          fontSize: '14px',
          marginBottom: '16px',
          padding: '12px 20px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          border: '1px dashed #333'
        }}>
          {t('screenClient.selectInstanceFirstHint')}
        </div>
      )}
      <SectionLabel style={!selectedInstance ? { opacity: 0.4 } : {}}>{t('screenClient.displaysSection')}</SectionLabel>
      <ScreenGrid style={!selectedInstance ? { opacity: 0.4, pointerEvents: 'none' } : {}}>
        {WALLS.map(wall => (
          <WallSection key={wall.id}>
            <WallLabel>
              {t('screenClient.wallRow', {
                id: wall.id,
                name: t(`screenClient.${WALL_COMPASS_KEY[wall.id] || 'wallNorth'}`),
              })}
            </WallLabel>
            <ScreenPair>
              {wall.screens.map(code => {
                const isTaken = screenStatuses[code];
                return (
                  <ScreenButton
                    key={code}
                    $taken={isTaken}
                    onClick={() => handleScreenSelect(code)}
                  >
                    {code}
                    {selectedInstance && (isTaken ? <TakenBadge /> : <AvailableBadge />)}
                  </ScreenButton>
                );
              })}
            </ScreenPair>
          </WallSection>
        ))}
      </ScreenGrid>

      <SectionLabel style={!selectedInstance ? { opacity: 0.4 } : {}}>{t('screenClient.ipadsSection')}</SectionLabel>
      <IPadGrid style={!selectedInstance ? { opacity: 0.4, pointerEvents: 'none' } : {}}>
        {VALID_IPAD_CODES.map(code => {
          const isTaken = screenStatuses[code];
          return (
            <IPadButton
              key={code}
              $taken={isTaken}
              onClick={() => handleScreenSelect(code)}
            >
              {code}
              {selectedInstance && (isTaken ? <TakenBadge /> : <AvailableBadge />)}
            </IPadButton>
          );
        })}
      </IPadGrid>
      </SelectorContainer>
    </>
  );
};

export default ScreenSelector;
